import React, { useEffect, useState } from "react";
import { Input } from "antd";
import {
  useAllOrders,
  useSearchOrders,
  useTrackOrder,
} from "../../../../../api/orders/orders";
import { useLoader } from "../../../../../utils/LoaderContext";

const ORDERS_PER_PAGE = 20;

const Live = ({ status = "live" }) => {
  const { setLoading } = useLoader();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 400);
    return () => clearTimeout(delay);
  }, [searchText]);

  const {
    data: allData,
    isLoading: loadingAll,
    isError,
  } = useAllOrders(
    { 
      currentPage, 
      ORDERS_PER_PAGE, 
      status,
      search: debouncedSearchText // Pass search to the API
    },
    {
      enabled: true, // Always enabled, let the API handle search
      keepPreviousData: true,
    }
  );

  const { mutate: trackOrder } = useTrackOrder();

  const isLoading = loadingAll;

  // Get orders directly from API response
  const orders = allData?.orders || [];
  
  // Get pagination info from API
  const totalPages = allData?.totalPages || 1;
  const totalOrders = allData?.totalOrders || 0;

  if (isError)
    return <p className="text-red-600 mt-4">Error fetching orders</p>;

  return (
    <div className="w-full mt-6">
      {/* Search */}
      <div className="mb-4 flex justify-between items-center">
        <Input
          className="w-80"
          placeholder="Search by AWB, Order Number or Name"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
        />
        <div className="text-sm text-gray-600">
          Total Orders: <strong>{totalOrders}</strong>
        </div>
      </div>

      <div className="overflow-x-auto hide-scrollbar">
        <table className="min-w-[1200px] w-full table-auto border border-gray-200">
          <thead className="bg-gray-100 text-left text-sm font-semibold text-gray-700">
            <tr>
              <th className="p-3">Order Date</th>
              <th className="p-3">AWB Number / Status</th>
              <th className="p-3">Payment</th>
              <th className="p-3">Products</th>
              <th className="p-3">Customer / Contact</th>
              <th className="p-3">Address</th>
              <th className="p-3">Courier / Tracking</th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {isLoading ? (
              <tr>
                <td colSpan={7}>
                  <div className="flex justify-center items-center py-10 text-gray-500 space-x-2">
                    <span className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></span>
                    <span>Loading orders...</span>
                  </div>
                </td>
              </tr>
            ) : orders.length > 0 ? (
              orders.map((order, index) => (
                <tr key={order._id || index} className="hover:bg-gray-50">
                  <td className="p-4">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                  <td className="p-4">
                    <div className="font-medium text-blue-600">
                      {order.awbNumber}
                    </div>
                    <div
                      className={`text-xs font-semibold px-2 py-1 rounded inline-block mt-1 ${
                        order.shippingStatusText === "cancelled"
                          ? "bg-red-100 text-red-600"
                          : order.shippingStatusText === "in_transit" ||
                            order.shippingStatusText === "In Transit"
                          ? "bg-blue-100 text-blue-600"
                          : order.shippingStatusText === "shipped"
                          ? "bg-green-100 text-green-600"
                          : order.shippingStatusText === "out_for_delivery" ||
                            order.shippingStatusText === "Out for Delivery"
                          ? "bg-yellow-100 text-yellow-600"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {order.shippingStatusText}
                    </div>
                  </td>
                  <td className="p-4">
                    ₹{order.InvoiceAmount}
                    <div className="text-xs mt-1 bg-green-100 text-green-700 inline-block px-2 py-1 rounded">
                      {order.PaymentType}
                    </div>
                  </td>
                  <td className="p-4">
                    {order.ProductDetails?.map((prod, i) => (
                      <p key={i} className="truncate">
                        {prod.Name || prod.name}
                      </p>
                    ))}
                  </td>
                  <td className="p-4">
                    <p>{order.CustomerName}</p>
                    <p>{order.BillingAddress?.Contact}</p>
                  </td>
                  <td className="p-4 text-sm">
                    <p>{order.BillingAddress?.City}</p>
                    <p>{order.BillingAddress?.State}</p>
                    <p>{order.BillingAddress?.Pincode}</p>
                  </td>
                  <td className="p-4">
                    <p>{order?.courier}</p>
                    {order.awbNumber ? (
                      <button
                        onClick={() => trackOrder(order.awbNumber)}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors duration-200"
                      >
                        Track Order
                      </button>
                    ) : (
                      <span className="inline-block px-3 py-1 bg-gray-100 text-gray-500 text-sm rounded">
                        AWB Not Available
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center py-6 text-gray-400">
                  {debouncedSearchText
                    ? "No orders found matching your search."
                    : "No live orders found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalOrders > 0 && (
        <div className="flex justify-between items-center mt-6 text-sm text-gray-700">
          <p>
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            {" "}({totalOrders} total orders)
          </p>
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || isLoading}
              className={`px-4 py-2 rounded border ${
                currentPage === 1 || isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
              }
              disabled={currentPage === totalPages || isLoading}
              className={`px-4 py-2 rounded border ${
                currentPage === totalPages || isLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Live;